import { createTheme } from '@mui/material/styles';
import {
  breakpoints as bpTokens,
  colorForMode,
  colors,
  density,
  motion,
  pxToRem,
  rounded,
  spacing as spacingTokens,
  typography as typeTokens,
} from './tokens';
import { VARIANT_MAPPING } from './typographyVariantMapping';

const fontStack =
  '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

function typeStyle(tokenName, overrides = {}) {
  const t = typeTokens[tokenName];
  if (!t) throw new Error(`Unknown typography token: ${tokenName}`);
  return {
    fontFamily: t.fontFamily === 'Inter' ? fontStack : t.fontFamily,
    fontSize: pxToRem(t.fontSizePx),
    fontWeight: t.fontWeight,
    lineHeight: t.lineHeight,
    ...(t.letterSpacing != null ? { letterSpacing: t.letterSpacing } : {}),
    ...overrides,
  };
}

/**
 * Legacy MUI variant styles preserved (D2) — do not flatten onto the new ramp in A3.
 * New ramp variants are exposed alongside for Track B migration.
 */
const LEGACY_TYPOGRAPHY = {
  fontSize: 15,
  h1: {
    fontSize: '2rem',
    fontWeight: 700,
    letterSpacing: '-0.025em',
    lineHeight: 1.2,
  },
  h2: {
    fontSize: '1.625rem',
    fontWeight: 700,
    letterSpacing: '-0.022em',
    lineHeight: 1.22,
  },
  h3: {
    fontSize: '1.375rem',
    fontWeight: 600,
    letterSpacing: '-0.02em',
    lineHeight: 1.28,
  },
  h4: {
    fontSize: '1.25rem',
    fontWeight: 600,
    letterSpacing: '-0.02em',
    lineHeight: 1.3,
  },
  h5: {
    fontSize: '1.125rem',
    fontWeight: 600,
    letterSpacing: '-0.015em',
    lineHeight: 1.33,
  },
  h6: {
    fontSize: '1.0625rem',
    fontWeight: 600,
    letterSpacing: '-0.012em',
    lineHeight: 1.4,
  },
  subtitle1: {
    fontSize: '0.9375rem',
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '-0.01em',
  },
  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '-0.005em',
  },
  body1: {
    fontSize: '0.9375rem',
    fontWeight: 400,
    lineHeight: 1.6,
  },
  body2: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.55,
  },
  button: {
    fontSize: '0.875rem',
    fontWeight: 500,
    textTransform: 'none',
    letterSpacing: '0.01em',
  },
  caption: {
    fontSize: '0.8125rem',
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.01em',
  },
  overline: {
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    lineHeight: 1.5,
    textTransform: 'uppercase',
  },
};

function buildTypography() {
  return {
    fontFamily: fontStack,
    htmlFontSize: 16,
    ...LEGACY_TYPOGRAPHY,
    // New DESIGN.md ramp — available now; call sites migrate in Track B (D2).
    display: typeStyle('display'),
    body: typeStyle('body'),
    'body-sm': typeStyle('body-sm'),
    micro: {
      ...typeStyle('micro'),
      textTransform: 'uppercase',
    },
    nano: {
      ...typeStyle('nano'),
      textTransform: 'uppercase',
    },
    metric: typeStyle('metric'),
    numeric: {
      ...typeStyle('numeric'),
      fontVariantNumeric: 'tabular-nums',
    },
    'numeric-sm': {
      ...typeStyle('numeric-sm'),
      fontVariantNumeric: 'tabular-nums',
    },
    kbd: typeStyle('kbd'),
  };
}

/**
 * @param {'light' | 'dark'} mode
 */
export function createAppTheme(mode = 'dark') {
  const isLight = mode === 'light';
  const c = (key) => colorForMode(key, mode);

  const focusInner = c('focus-inner');
  const focusOuter = c('focus-outer');
  const dualFocus = {
    outline: 'none',
    /* design-system-focus-ring */
    boxShadow: `0 0 0 2px ${focusInner}, 0 0 0 4px ${focusOuter}`,
  };

  const durationMs = {
    instant: motion.instant,
    fast: motion.fast,
    base: motion.base,
    slow: motion.slow,
  };

  const typography = buildTypography();

  return createTheme({
    breakpoints: {
      // IC-3 / E0.5: values only — never supply a hand-ordered `keys` array.
      values: { ...bpTokens },
    },
    // D1: keep MUI defaults (spacing unit 8, shape.borderRadius 4).
    // The 4px design scale lives on theme.ef.spacing / tokens.spacing — Track B codemod only.
    spacing: 8,
    shape: {
      borderRadius: 4,
    },
    palette: {
      mode,
      primary: {
        main: c('accent'),
        dark: isLight ? colors['accent-light'] : colors.accent,
        light: isLight ? colors.accent : colors['accent-light'],
        // P2: text-primary on accent fails AA (~2.3:1). Canvas clears 7:1 on accent.
        contrastText: c('canvas'),
      },
      secondary: {
        main: c('text-secondary'),
      },
      error: { main: c('status-crit') },
      warning: { main: c('status-warn') },
      success: { main: c('status-ok') },
      info: { main: c('status-info') },
      background: {
        default: c('canvas'),
        paper: c('panel'),
      },
      text: {
        primary: c('text-primary'),
        secondary: c('text-secondary'),
        disabled: c('text-disabled'),
      },
      divider: c('hair'),
      action: {
        hover: c('soft'),
        selected: c('accent-surface'),
        disabled: c('text-disabled'),
        focus: c('accent-surface'),
      },
      // Semantic extensions (consume via theme.palette.ef.*)
      ef: {
        canvas: c('canvas'),
        recessed: c('recessed'),
        panel: c('panel'),
        chrome: c('chrome'),
        soft: c('soft'),
        hair: c('hair'),
        edge: c('edge'),
        muted: c('text-muted'),
        accentSurface: c('accent-surface'),
        accentEdge: c('accent-edge'),
        scrim: c('scrim'),
        focusInner,
        focusOuter,
        data: [1, 2, 3, 4, 5, 6].map((i) => c(`data-${i}`)),
        heat: [0, 1, 2, 3, 4].map((i) => c(`heat-${i}`)),
      },
    },
    typography,
    transitions: {
      duration: {
        shortest: durationMs.instant,
        shorter: durationMs.fast,
        short: durationMs.base,
        standard: durationMs.base,
        complex: durationMs.slow,
        enteringScreen: durationMs.base,
        leavingScreen: durationMs.fast,
      },
      easing: {
        easeInOut: motion.ease,
        easeOut: motion['ease-out'],
        sharp: motion.ease,
        easeIn: motion.ease,
      },
    },
    ef: {
      spacing: spacingTokens,
      rounded,
      motion,
      density,
      measure: spacingTokens.measure,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            scrollBehavior: 'smooth',
          },
          body: {
            fontFamily: fontStack,
            backgroundColor: c('canvas'),
            color: c('text-primary'),
            transition: `background-color ${durationMs.base}ms ${motion.ease}, color ${durationMs.base}ms ${motion.ease}`,
          },
          // Dual-ring focus (DESIGN.md §Focus) — at least one ring clears 3:1 on any fill.
          '*:focus-visible': dualFocus,
          // Reduced-motion four-part contract (EXPERIENCE.md): durations → instant;
          // no keyframes; scroll-behavior auto; (streaming batching is carrier-level / A4).
          '@media (prefers-reduced-motion: reduce)': {
            html: { scrollBehavior: 'auto' },
            '*, *::before, *::after': {
              animationDuration: '0.01ms !important',
              animationIterationCount: '1 !important',
              transitionDuration: '0.01ms !important',
              scrollBehavior: 'auto !important',
            },
          },
          '.stack-tabular-nums': {
            fontVariantNumeric: 'tabular-nums',
            fontFamily: typeTokens.numeric.fontFamily,
          },
        },
      },
      MuiTypography: {
        defaultProps: {
          // New ramp only — legacy variants keep MUI default elements (D2: no subtitle1→h3).
          variantMapping: {
            ...VARIANT_MAPPING,
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: c('panel'),
            background: c('panel'),
            boxShadow: 'none',
            borderRadius: rounded.none,
            transition: `background-color ${durationMs.base}ms ${motion.ease}, color ${durationMs.base}ms ${motion.ease}, border-color ${durationMs.base}ms ${motion.ease}`,
          },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: c('panel'),
            boxShadow: 'none',
            border: `1px solid ${c('edge')}`,
            borderRadius: pxToRem(rounded.md),
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            backgroundColor: c('chrome'),
            background: c('chrome'),
            boxShadow: 'none',
            borderColor: c('edge'),
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
          },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'transparent' },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: c('chrome'),
            background: c('chrome'),
            boxShadow: 'none',
            borderBottom: `1px solid ${c('hair')}`,
            color: c('text-primary'),
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          root: {
            '& .MuiBackdrop-root': {
              backgroundColor: c('scrim'),
            },
          },
          paper: {
            backgroundImage: 'none',
            backgroundColor: c('panel'),
            background: c('panel'),
            boxShadow: 'none',
            border: `1px solid ${c('edge')}`,
            borderRadius: pxToRem(rounded.lg),
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            backgroundColor: c('panel'),
            background: c('panel'),
            boxShadow: 'none',
            border: `1px solid ${c('edge')}`,
            borderRadius: pxToRem(rounded.md),
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            backgroundColor: c('panel'),
            background: c('panel'),
            boxShadow: 'none',
            border: `1px solid ${c('edge')}`,
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            backgroundColor: c('panel'),
            background: c('panel'),
            boxShadow: 'none',
            border: `1px solid ${c('edge')}`,
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true, disableRipple: true },
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: pxToRem(rounded.md),
            minHeight: 32,
            boxShadow: 'none',
            fontSize: LEGACY_TYPOGRAPHY.button.fontSize,
            fontWeight: 600,
            // P1: variant boxShadow:none must not wipe dual-ring on focus.
            '&:focus-visible': dualFocus,
          },
          containedPrimary: {
            backgroundColor: c('accent-surface'),
            color: c('text-primary'),
            border: `1px solid ${c('accent-edge')}`,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: c('accent-surface'),
              boxShadow: 'none',
              filter: 'brightness(1.06)',
            },
            '&:focus-visible': dualFocus,
          },
          outlinedPrimary: {
            backgroundColor: 'transparent',
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: c('edge'),
            color: c('text-primary'),
            '&:hover': {
              backgroundColor: c('soft'),
              borderColor: c('edge'),
              boxShadow: 'none',
            },
            '&:focus-visible': dualFocus,
          },
          textPrimary: {
            color: c('accent'),
            '&:focus-visible': dualFocus,
          },
        },
      },
      MuiButtonBase: {
        defaultProps: { disableRipple: true },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            // D3: role-aware density deferred to A4 (supported DataGrid density APIs).
            border: 'none',
            backgroundColor: 'transparent',
          },
          columnHeaders: {
            borderBottom: `1px solid ${c('edge')}`,
          },
          cell: {
            borderBottom: `1px solid ${c('hair')}`,
          },
        },
      },
    },
  });
}

/** @deprecated Prefer createAppTheme(mode). Kept for import stability. */
export const lightTheme = createAppTheme('light');
/** @deprecated Prefer createAppTheme(mode). Kept for import stability. */
export const darkTheme = createAppTheme('dark');
